import { Modal as AntModal, Row } from 'antd';
import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import Text from 'components/Text';
import StaffAvatar from 'components/StaffAvatar';
import shopActions from 'services/actions/shop';
import shopSelectors from 'services/selectors/shop';
import { IStaffGroupAssignment } from 'services/types/staff-group';
import { useAppDispatch } from 'store/hooks';
import { IStaffGroupUI } from 'types/staff-group';
import { readableTextOn } from 'utils/color-contrast';
import { modalConfirmRef } from 'widgets/ModalConfirm';
import { ANYONE_ID } from 'Calendar/anyone';
import GroupEditor from './GroupEditor';

type Props = {};
type Ref = {
  open: () => void;
  close: () => void;
};
export const useModalStaffGroupsRef = () => useRef<Ref>(null);

/** Group being edited in the editor pane: an existing group, a blank new one, or nothing. */
type EditorState = { mode: 'create' } | { mode: 'edit'; group: IStaffGroupUI } | null;

const ModalStaffGroups = forwardRef<Ref, Props>(({ }, ref) => {
  const dispatch = useAppDispatch();
  const groups = shopSelectors.staffGroups();
  const groupsByStaffId = shopSelectors.staffGroupsByStaffId();
  const roster = shopSelectors.pureStaffs();
  const staffs = useMemo(() => roster
    .filter(staff => staff.id !== ANYONE_ID)
    // Alphabetical: the roster arrives in shop order, which makes a staff hard to find in a long list.
    .sort((a, b) => (a.name || '').localeCompare(b.name || '')), [roster]);

  const [visible, setVisible] = useState(false);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState>(null);
  // staffId -> groupId ('' = ungrouped). Only staff the user actually touched appear here, which is
  // also exactly what the delta assignment endpoint wants.
  const [pending, setPending] = useState<Record<string, string>>({});

  const handleOpen = () => {
    setPending({});
    setEditor(null);
    setSelectedGroupId(null);
    setVisible(true);
  };
  const handleClose = () => {
    setPending({});
    setEditor(null);
    setVisible(false);
  };

  useImperativeHandle(ref, () => ({
    open: handleOpen,
    close: handleClose,
  }));

  // Nothing can be assigned until a group is selected, so never leave the modal in that state:
  // select the first group as soon as one exists, including the one just created.
  useEffect(() => {
    if (!visible) return;
    if (selectedGroupId && groups.some(group => group.id === selectedGroupId)) return;
    setSelectedGroupId(groups.length ? groups[0].id : null);
  }, [visible, groups, selectedGroupId]);

  const groupsById = useMemo(() => {
    const map: Record<string, IStaffGroupUI> = {};
    groups.forEach(group => { map[group.id] = group; });
    return map;
  }, [groups]);

  const selectedGroup = selectedGroupId ? groupsById[selectedGroupId] : undefined;

  /** Group a staff currently belongs to, with any unsaved change applied on top. */
  const resolveGroupId = (staffId: string): string => {
    const pendingValue = pending[staffId];
    if (pendingValue !== undefined) return pendingValue;
    return groupsByStaffId[staffId]?.id || '';
  };

  const memberCount = useMemo(() => {
    const counts: Record<string, number> = {};
    staffs.forEach(staff => {
      const groupId = resolveGroupId(staff.id);
      if (groupId) counts[groupId] = (counts[groupId] || 0) + 1;
    });
    return counts;
  }, [staffs, groupsByStaffId, pending]);

  const toggleStaff = (staffId: string) => {
    if (!selectedGroupId) return;
    const current = resolveGroupId(staffId);
    // Tapping a member of the selected group removes them; anyone else moves into it.
    setPending(prev => ({ ...prev, [staffId]: current === selectedGroupId ? '' : selectedGroupId }));
  };

  const handleSubmitEditor = (values: { name: string; colorHex: string }) => {
    if (editor?.mode === 'edit') {
      dispatch(shopActions.updateStaffGroup({ id: editor.group.id, ...values }));
    } else {
      dispatch(shopActions.createStaffGroup(values));
    }
    setEditor(null);
  };

  const handleDelete = (group: IStaffGroupUI) => {
    const count = memberCount[group.id] || 0;
    modalConfirmRef.current?.open(
      () => {
        dispatch(shopActions.deleteStaffGroup(group.id));
        if (selectedGroupId === group.id) setSelectedGroupId(null);
        // Drop pending assignments into this group: the delta endpoint 404s the whole batch on an
        // unknown groupId, which would silently discard every other reassignment too.
        setPending(prev => Object.fromEntries(
          Object.entries(prev).filter(([, groupId]) => groupId !== group.id),
        ));
      },
      'Delete group',
      `Delete "${group.name}"? ${count} staff will keep working as usual, their columns just lose the color.`,
    );
  };

  /** Skips staff the user tapped back to where they started. */
  const resolveGroupIdChanged = (staffId: string, groupId: string) =>
    (groupsByStaffId[staffId]?.id || '') !== groupId;

  const handleUpdate = () => {
    const assignments: IStaffGroupAssignment[] = Object.entries(pending)
      .filter(([staffId, groupId]) => resolveGroupIdChanged(staffId, groupId))
      .map(([staffId, groupId]) => ({ staffId: +staffId, groupId: groupId ? +groupId : null }))
      .filter(o => Number.isFinite(o.staffId) && (o.groupId === null || Number.isFinite(o.groupId)));
    if (assignments.length) dispatch(shopActions.assignStaffGroups(assignments));
    handleClose();
  };

  return (
    <AntModal
      open={visible}
      onCancel={handleClose}
      centered
      title={null}
      footer={false}
      closable={false}
      destroyOnClose={false}
      width={'70vw'}
    >
      <Header justify={'space-between'}>
        <Text className="title">Staff Groups</Text>
      </Header>
      <ContainerContent>
        <Panes>
          <Pane>
            <PaneHeader>
              <Text variant="H8" color="text_3">Groups</Text>
              <button className="btn-add" type="button" onClick={() => setEditor({ mode: 'create' })}>
                <Text className="label">+ NEW GROUP</Text>
              </button>
            </PaneHeader>
            {editor && (
              <GroupEditor
                group={editor.mode === 'edit' ? editor.group : undefined}
                onSubmit={handleSubmitEditor}
                onCancel={() => setEditor(null)}
              />
            )}
            {!groups.length && !editor && (
              <EmptyHint>
                <Text variant="BODY_1" color="text_3">
                  No groups yet. Create one, pick its color, then tap the staff who belong to it.
                </Text>
              </EmptyHint>
            )}
            {groups.map(group => (
              <GroupRow
                key={group.id}
                $active={group.id === selectedGroupId}
                onClick={() => setSelectedGroupId(group.id === selectedGroupId ? null : group.id)}
              >
                <ColorChip $color={group.colorHex} />
                <div style={{ flex: 1 }}>
                  <Text variant="H8" color="text_3">{group.name}</Text>
                  <Text variant="CAPTION_1" color="text_3">
                    {group.id === selectedGroupId
                      ? `Selected · ${memberCount[group.id] || 0} staff`
                      : `${memberCount[group.id] || 0} staff · tap to select`}
                  </Text>
                </div>
                <button
                  className="btn-link"
                  type="button"
                  onClick={e => { e.stopPropagation(); setEditor({ mode: 'edit', group }); }}
                >
                  <Text className="label">EDIT</Text>
                </button>
                <button
                  className="btn-link btn-danger"
                  type="button"
                  onClick={e => { e.stopPropagation(); handleDelete(group); }}
                >
                  <Text className="label">DELETE</Text>
                </button>
              </GroupRow>
            ))}
          </Pane>

          <Pane>
            <PaneHeader>
              {selectedGroup ? (
                <AssigningTo>
                  <ColorChip $color={selectedGroup.colorHex} />
                  <Text variant="H8" color="text_3">
                    Tap a staff to add or remove — <strong>{selectedGroup.name}</strong>
                  </Text>
                </AssigningTo>
              ) : (
                <Text variant="H8" color="text_3">Create a group first</Text>
              )}
            </PaneHeader>
            {staffs.map(staff => {
              const groupId = resolveGroupId(staff.id);
              const group = groupsById[groupId];
              return (
                <StaffRow
                  key={staff.id}
                  $disabled={!selectedGroupId}
                  onClick={() => toggleStaff(staff.id)}
                >
                  <StaffAvatar name={staff.name} avatar={staff.avatar} size={36} fontSize={14} />
                  <Text style={{ flex: 1 }} variant="H8" color="text_3">{staff.name}</Text>
                  {group && (
                    <GroupTag $color={group.colorHex}>
                      <span style={{ color: readableTextOn(group.colorHex) }}>{group.name}</span>
                    </GroupTag>
                  )}
                </StaffRow>
              );
            })}
          </Pane>
        </Panes>
        <Footer>
          <button className="btn btn-delete" type="button" onClick={handleClose}>
            <Text className="label">CLOSE</Text>
          </button>
          <button className="btn btn-update" type="button" onClick={handleUpdate}>
            <Text className="label">UPDATE</Text>
          </button>
        </Footer>
      </ContainerContent>
    </AntModal>
  );
});
ModalStaffGroups.displayName = 'ModalStaffGroups';
export default ModalStaffGroups;

const Header = styled(Row)`
  height: 50px;
  position: relative;
  align-items: center;
  padding: 8px;
  border-top-right-radius: 5px;
  border-top-left-radius: 5px;
  border-bottom: 1px solid #148CF1;
  background: #E8F3FF;
  .title {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    color: #1D2129;
    font-family: Poppins;
    font-size: 20px;
    font-weight: 600;
    line-height: 24px;
  }
`;

const ContainerContent = styled.div`
  max-height: 78vh;
  min-height: 78vh;
  display: flex;
  flex-direction: column;
  background: #F8F9FA;
`;

const Panes = styled.div`
  flex: 1;
  display: flex;
  gap: 16px;
  padding: 16px;
  overflow: hidden;
`;

const Pane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
  overflow-y: auto;
`;

const PaneHeader = styled(Row)`
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  .btn-add {
    height: 44px;
    padding: 0 14px;
    border-radius: 2px;
    background: #232F3E;
    .label { color: #fff; font-size: 15px; font-weight: 600; }
  }
`;

const EmptyHint = styled.div`
  padding: 16px;
  background: #fff;
  border: 1px dashed #E5E6EB;
  border-radius: 6px;
`;

const GroupRow = styled.div<{ $active: boolean }>`
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 60px;
  padding: 8px 12px;
  background: #fff;
  border-radius: 6px;
  border: ${p => p.$active ? '2px solid #148CF1' : '1px solid #E5E6EB'};

  .btn-link {
    height: 44px;
    padding: 0 10px;
    .label { font-size: 15px; font-weight: 600; color: #0085E7; }
  }
  .btn-danger .label { color: #E03B3B; }
`;

const ColorChip = styled.span<{ $color: string }>`
  width: 28px;
  height: 28px;
  border-radius: 6px;
  background: ${p => p.$color};
  flex-shrink: 0;
`;

const StaffRow = styled.div<{ $disabled: boolean }>`
  cursor: ${p => p.$disabled ? 'not-allowed' : 'pointer'};
  display: flex;
  align-items: center;
  gap: 12px;
  min-height: 56px;
  padding: 8px 12px;
  background: #fff;
  border: 1px solid #E5E6EB;
  border-radius: 6px;
  opacity: ${p => p.$disabled ? 0.6 : 1};
`;

const AssigningTo = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const GroupTag = styled.span<{ $color: string }>`
  padding: 4px 10px;
  border-radius: 999px;
  background: ${p => p.$color};
  font-size: 14px;
  font-weight: 600;
`;

const Footer = styled(Row)`
  padding: 0.75rem 1.5rem;
  gap: 1rem;
  background-color: #fff;
  .btn {
    display: flex;
    height: 48px;
    justify-content: center;
    align-items: center;
    flex: 1 0 0;
    .label { font-size: 18px; font-weight: 600; }
  }
  .btn-delete {
    border-radius: 2px;
    border: 1px solid #7B7B7B;
    background: #fff;
    .label { color: #7B7B7B; }
  }
  .btn-update {
    border-radius: 2px;
    background: #232F3E;
    .label { color: #FFF; }
  }
`;
